/**
 * frontend/src/tipos/tiposAutenticacao.ts
 *
 * Tipos TypeScript para autenticação do frontend.
 *
 * CONTEXTO:
 * Estes tipos descrevem os contratos trocados entre frontend e backend
 * durante os fluxos de cadastro/login e também são usados no contexto
 * de autenticação local (por exemplo, Context API ou localStorage).
 *
 * PADRÕES:
 * - Nomes longos e descritivos
 * - Timestamps vindos da API são strings ISO-8601
 * - Não incluir senha em interfaces de usuário persistentes
 */

/**
 * Representa um usuário no sistema (SEM campo de senha).
 *
 * Observações:
 * - Todos os timestamps são strings ISO-8601 para facilitar serialização JSON
 * - Campos opcionais que o backend pode enviar como null são tipados como `string | null`
 */
export interface Usuario {
  /** Identificador único (UUID ou similar) */
  id: string;
  /** Email do usuário (único) */
  email: string;
  /** Nome completo do usuário */
  nome: string;
  /** Empresa do usuário quando aplicável; pode ser null */
  empresa: string | null;
  /** Cargo ou função do usuário quando aplicável; pode ser null */
  cargo: string | null;
  /** Data de criação do usuário (ISO 8601) */
  data_criacao: string;
  /** Data da última atualização do usuário (ISO 8601) */
  data_atualizacao: string;
  /** Indicador se o usuário está ativo */
  ativo: boolean;
  /** Role do usuário: 'admin' ou 'user' (opcional para retrocompatibilidade) */
  role?: 'admin' | 'user';
  /** ID da organização primária do usuário (MULTI-TENANT) */
  organizacao_id?: string | null;
  /** Role do usuário na organização primária (MULTI-TENANT) */
  role_organizacao?: 'owner' | 'admin' | 'member' | null;
  /** Nome da organização primária para exibição (MULTI-TENANT) */
  nome_organizacao?: string | null;
}

/**
 * Dados necessários para efetuar um cadastro (enviados ao backend).
 * A senha nunca é retornada nas interfaces Usuario.
 */
export interface DadosCadastro {
  email: string;
  senha: string;
  nome: string;
  /** Campos opcionais do backend podem ser omitidos no cadastro */
  empresa?: string;
  cargo?: string;
}

/**
 * Credenciais para login.
 */
export interface DadosLogin {
  email: string;
  senha: string;
}

/**
 * Resposta do endpoint de login (contrato com o backend).
 * - access_token: token JWT de acesso (curta duração)
 * - refresh_token: token JWT para renovação (longa duração)
 * - token_type: geralmente "bearer"
 * - usuario: objeto Usuario (sem senha)
 * - expires_in: tempo em segundos até expiração do access_token
 */
export interface RespostaLogin {
  access_token: string;
  refresh_token: string;
  token_type: string; // ex: "bearer"
  usuario: Usuario;
  expires_in: number; // segundos
}

/**
 * Resposta do endpoint de refresh token.
 */
export interface RespostaRefreshToken {
  access_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Resposta do endpoint de cadastro de usuário.
 */
export interface RespostaCadastro {
  usuario: Usuario;
  mensagem: string;
}

/**
 * Representa o usuário autenticado no cliente (por exemplo: estado do Contexto).
 * - token: string de autenticação (access_token)
 * - refreshToken: token para renovação (longa duração)
 * - expiresAt: Date calculada a partir de expires_in
 */
export interface UsuarioAutenticado {
  usuario: Usuario;
  token: string;
  refreshToken: string;
  expiresAt: Date;
}

/**
 * Type guard em runtime para verificar se um objeto é UsuarioAutenticado.
 * Uso: if (isUsuarioAutenticado(obj)) { // obj agora é UsuarioAutenticado }
 */
export function isUsuarioAutenticado(obj: unknown): obj is UsuarioAutenticado {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  const usuario = o.usuario;
  if (!usuario || typeof usuario !== 'object' || usuario === null) return false;
  const u = usuario as Record<string, unknown>;
  const hasUsuario = typeof u.id === 'string' && typeof u.email === 'string';
  const hasToken = typeof o.token === 'string' && (o.token as string).length > 0;
  const expiresAt = o.expiresAt;
  const hasExpiresAt = expiresAt instanceof Date || (typeof expiresAt === 'string' && !isNaN(Date.parse(expiresAt as string)));
  return hasUsuario && hasToken && hasExpiresAt;
}

/**
 * Calcula a data de expiração a partir de `expiresIn` (segundos).
 * Retorna um objeto Date local que representa o instante de expiração.
 *
 * Exemplo:
 * const expiresAt = calcularExpiracao(3600); // 1 hora a partir de agora
 */
export function calcularExpiracao(expiresIn: number): Date {
  const ms = Math.max(0, expiresIn) * 1000;
  return new Date(Date.now() + ms);
}

/**
 * Helper que constrói um UsuarioAutenticado a partir da resposta de login
 * e calcula a data de expiração automaticamente.
 */
export function usuarioAutenticadoFromRespostaLogin(res: RespostaLogin): UsuarioAutenticado {
  return {
    usuario: res.usuario,
    token: res.access_token,
    refreshToken: res.refresh_token,
    expiresAt: calcularExpiracao(res.expires_in),
  };
}
